/**
 * `/presence` — the live presence board, Task PW1 (pillar web), consuming the Task P2
 * presence API. `web/shapes/presence.md` is the agreed shape, written before this file.
 *
 * The headline "manage the workers + where" view: a manager sees their whole subtree's
 * current state; a plain employee sees only their own single row — the server's
 * `resolved` scope decides that (`nav/menu.ts`'s own rule), and this screen renders
 * whatever it is handed. `nav/menu.ts` gates the item on
 * `[workforce.view_self, workforce.view_department, workforce.view_workspace]`, the
 * same anyOf floor `/attendance` uses, with no role check.
 *
 * ── Not a productivity score ──────────────────────────────────────────────────
 *
 * `web/shapes/presence.md` §2, and stated on screen in both languages
 * (`presence.intro`): this is a coordination-status read. There is no sort by "most
 * productive," no ranking, and the geo `trust` flag (`suspect`/`unverified`) is
 * rendered with the same neutral, `info`-severity treatment whatever its value —
 * never louder for `suspect` than for `trusted`.
 *
 * ── Live, via `usePolledQuery` ─────────────────────────────────────────────────
 *
 * Both reads poll at `PRESENCE_POLL_MS`, the same cadence `OverviewScreen`'s
 * `LIVE_POLL_MS` uses for running timers and for the identical reason: presence's two
 * working statuses are anchored to an open task timer somebody starts and stops on
 * their own schedule, not to an hourly aggregate. `usePolledQuery` keeps the last
 * successful answer on screen through a failed refresh (`staleAfterError`), so a
 * dropped poll cannot blank four tiles a manager was mid-glance at.
 */

import { useState, type ReactNode, type SyntheticEvent } from 'react';

import { useI18n } from '../i18n';
import { DepartmentSelect } from '../components/DepartmentSelect';
import { formatInstantWithAge } from '../i18n/datetime';
import { useSession } from '../auth/SessionProvider';
import {
  presencePath,
  presenceSummaryPath,
  PRESENCE_STATUSES,
  type PresenceListResponse,
  type PresenceStatus,
  type PresenceSummary,
} from '../api/presence';
import { usePolledQuery } from '../api/usePolledQuery';
import { PRESENCE_STATUS_PRESENTATION, PRESENCE_TRUST_LABEL } from '../presence/status';
import { ApiErrorNote } from '../states/ApiErrorNote';
import { Badge } from '../states/Badge';
import { EmptyState } from '../states/EmptyState';
import { LastSync } from '../states/LastSync';
import { Icon } from '../states/icons';
import { Skeleton } from '../states/Skeleton';
import { ScreenHeading } from '../shell/ScreenHeading';
import { PresenceStatusBadge } from './presence/PresenceStatusBadge';

/**
 * The poll interval. `web/shapes/presence.md` §6: the same reasoning
 * `OverviewScreen.tsx`'s `LIVE_POLL_MS` gives for running timers — a person changes
 * their own status by starting a timer or checking in, not on a fixed schedule.
 */
export const PRESENCE_POLL_MS = 60_000;

export function PresenceScreen(): ReactNode {
  const { t, locale } = useI18n();
  const { user } = useSession();
  const timezone = user?.timezone ?? 'UTC';

  const [statusFilter, setStatusFilter] = useState<PresenceStatus | ''>('');
  const [departmentId, setDepartmentId] = useState<string | null>(null);
  const [officeInput, setOfficeInput] = useState('');
  const [officeLocationId, setOfficeLocationId] = useState<string | null>(null);

  const listPath = presencePath({
    status: statusFilter === '' ? undefined : statusFilter,
    officeLocationId: officeLocationId ?? undefined,
    departmentId: departmentId ?? undefined,
  });
  // The summary endpoint accepts only department_id (see presenceSummaryPath): the
  // office filter narrows the list, not the tiles.
  const summaryPath = presenceSummaryPath({
    departmentId: departmentId ?? undefined,
  });

  const list = usePolledQuery<PresenceListResponse>(listPath, PRESENCE_POLL_MS);
  const summary = usePolledQuery<PresenceSummary>(summaryPath, PRESENCE_POLL_MS);

  const rows = list.data?.data ?? [];
  const fetchedAt = Math.max(list.fetchedAt ?? 0, summary.fetchedAt ?? 0);
  const refreshing = list.refreshing || summary.refreshing;

  return (
    <div className="wp-daysheet">
      <header className="wp-daysheet__head">
        <div className="wp-daysheet__date">
          <ScreenHeading>{t('nav.presence')}</ScreenHeading>
          <p className="wp-daysheet__weekday" data-testid="pw1-intro">
            {t('presence.intro')}
          </p>
        </div>
        <div className="wp-daysheet__stamps" aria-live="polite" data-testid="pw1-updated-at">
          {/* `PRODUCT.md` §4's ninth row. The agent stamp is this screen's own newest
              successful read; there is no achau sweep behind presence, and the
              component renders that absence as "chưa từng" rather than hiding the
              row — an unlabelled single "last updated" is exactly what §4 forbids. */}
          <LastSync
            agentSyncAt={fetchedAt === 0 ? null : new Date(fetchedAt).toISOString()}
            achauSyncAt={null}
            timezone={timezone}
            testId="pw1-lastsync"
          />
          {refreshing ? <p className="wp-small wp-muted">{t('presence.updating')}</p> : null}
        </div>
      </header>

      {/* ── Summary tiles ────────────────────────────────────────────────────── */}
      <section
        className="wp-section"
        aria-labelledby="pw1-summary-heading"
        data-testid="pw1-summary"
      >
        <h2 className="wp-section__title" id="pw1-summary-heading">
          {t('presence.summary.heading')}
        </h2>

        {summary.status === 'loading' ? (
          <Skeleton
            rows={4}
            variant="block"
            label={t('presence.summary.loading')}
            testId="pw1-summary-skeleton"
          />
        ) : null}

        {summary.error !== null && summary.data === null ? (
          <ApiErrorNote
            error={summary.error}
            onRetry={summary.reload}
            subject={t('presence.summary.subject')}
            testId="pw1-summary-error"
          />
        ) : null}

        {(() => {
          // Captured into a local rather than read as `summary.data` inside the
          // `.map` closure below: a **measured** count is shown for all four
          // statuses whenever this branch renders, never a `0` stood in for one
          // TypeScript could not otherwise prove present.
          const summaryData = summary.data;
          if (summaryData === null) return null;
          return (
            <>
              <div className="wp-presence__summary" data-testid="pw1-summary-tiles">
                {PRESENCE_STATUSES.map((status) => (
                  <section
                    key={status}
                    className="wp-tile"
                    data-testid={`pw1-summary-${status}`}
                    aria-labelledby={`pw1-summary-${status}-title`}
                  >
                    <h3 className="wp-tile__title" id={`pw1-summary-${status}-title`}>
                      {t(PRESENCE_STATUS_PRESENTATION[status].labelKey)}
                    </h3>
                    <div className="wp-tile__figure" data-testid={`pw1-summary-${status}-figure`}>
                      {summaryData[status]}
                    </div>
                  </section>
                ))}
              </div>
              <p className="wp-small wp-muted" data-testid="pw1-summary-total">
                {t('presence.summary.total', { count: summaryData.total })}
              </p>
              {summary.staleAfterError ? (
                <p className="wp-small" data-testid="pw1-summary-stale">
                  {t('presence.summary.staleAfterError')}
                </p>
              ) : null}
            </>
          );
        })()}
      </section>

      {/*
        ── Filters — every one a server parameter, never applied client-side ───

        One bar, on the shape `/devices` and `/attendance` already wear: every key
        above its own control, every control 44px, and **one** action pair at the end
        of the row. What stood here before had the legend and the first label stacked
        flush on two lines, the next two labels sitting to the *left* of their boxes,
        and an "Áp dụng" button per field — three alignments and two identical buttons
        in one row (finish review, M5).

        The two pickers apply on change, exactly as `/attendance`'s department picker
        does: choosing from a list *is* the decision, and a board that re-reads itself
        every minute should not also wait for a second click. "Áp dụng" belongs to the
        one field a person types — the office id — and "Đọc lại" re-reads both queries
        without changing the filter.
      */}
      <form
        className="wp-presence__filters"
        data-testid="pw1-filters"
        onSubmit={(event: SyntheticEvent) => {
          event.preventDefault();
          const trimmed = officeInput.trim();
          setOfficeLocationId(trimmed === '' ? null : trimmed);
        }}
      >
        <fieldset className="wp-presence__filters-group">
          {/* The key of the row, kept for a screen reader and taken off the paper: in
              print it was an eyebrow above the first field's own label saying the same
              thing twice (finish review, minor b). */}
          <legend className="wp-visually-hidden">{t('presence.filters.legend')}</legend>

          <div className="wp-field">
            <label className="wp-field__label" htmlFor="pw1-status">
              {t('presence.filter.status')}
            </label>
            <div className="wp-select">
              <select
                id="pw1-status"
                className="wp-field__input"
                value={statusFilter}
                onChange={(event) => {
                  setStatusFilter(event.target.value as PresenceStatus | '');
                }}
                data-testid="pw1-status"
              >
                <option value="">{t('presence.filter.all')}</option>
                {PRESENCE_STATUSES.map((value) => (
                  <option key={value} value={value}>
                    {t(PRESENCE_STATUS_PRESENTATION[value].labelKey)}
                  </option>
                ))}
              </select>
              <Icon name="chevron-down" className="wp-select__chevron" />
            </div>
          </div>

          {/* A picker, not a typed id. `GET /employees/departments` has been mounted
              since Task T2-A and the shared control reads it — the same one
              `/attendance`, `/devices` and `/overview` already use. */}
          <DepartmentSelect
            id="pw1-department"
            testId="pw1-department"
            label={t('presence.filter.department')}
            value={departmentId}
            onChange={setDepartmentId}
          />

          <div className="wp-field">
            <label className="wp-field__label" htmlFor="pw1-office">
              {t('presence.filter.office')}
            </label>
            <input
              id="pw1-office"
              className="wp-field__input"
              name="office_location_id"
              inputMode="numeric"
              value={officeInput}
              aria-describedby="pw1-office-hint"
              onChange={(event) => {
                setOfficeInput(event.target.value);
              }}
              data-testid="pw1-office"
            />
            <p className="wp-small wp-muted" id="pw1-office-hint">
              {t('presence.filter.officeHint')}
            </p>
          </div>
        </fieldset>

        <div className="wp-presence__filters-actions">
          <button type="submit" className="wp-button" data-testid="pw1-apply">
            {t('presence.filter.apply')}
          </button>
          <button
            type="button"
            className="wp-button wp-button--quiet"
            data-testid="pw1-reload"
            onClick={() => {
              list.reload();
              summary.reload();
            }}
          >
            {t('presence.filter.reload')}
          </button>
        </div>
      </form>

      {/* ── The live list ────────────────────────────────────────────────────── */}
      <section className="wp-section" aria-labelledby="pw1-list-heading">
        <h2 className="wp-section__title" id="pw1-list-heading">
          {t('presence.list.heading')}
        </h2>

        {list.status === 'loading' ? (
          <Skeleton
            rows={4}
            variant="block"
            label={t('presence.list.loading')}
            testId="pw1-list-skeleton"
          />
        ) : null}

        {list.error !== null && list.data === null ? (
          <ApiErrorNote
            error={list.error}
            onRetry={list.reload}
            subject={t('presence.list.subject')}
            testId="pw1-list-error"
          />
        ) : null}

        {list.data !== null && rows.length === 0 ? (
          <EmptyState
            title={t('presence.list.empty.title')}
            description={t('presence.list.empty.body')}
            testId="pw1-list-empty"
          />
        ) : null}

        {list.staleAfterError ? (
          <p className="wp-small" data-testid="pw1-list-stale">
            {t('presence.list.staleAfterError')}
          </p>
        ) : null}

        {list.data !== null && rows.length > 0 ? (
          <div className="wp-table-scroll">
            <table className="wp-table" data-testid="pw1-list">
              <thead>
                <tr>
                  <th scope="col">{t('presence.list.col.employee')}</th>
                  <th scope="col">{t('presence.list.col.status')}</th>
                  <th scope="col">{t('presence.list.col.where')}</th>
                  <th scope="col">{t('presence.list.col.onWhat')}</th>
                  <th scope="col">{t('presence.list.col.lastSeen')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.employee_id} data-testid={`pw1-row-${row.employee_id}`}>
                    <th scope="row">
                      <span className="wp-presence__who">
                        <span>{row.full_name}</span>
                        <span className="wp-small wp-muted">
                          {row.department ?? t('presence.noDepartment')}
                        </span>
                      </span>
                    </th>
                    <td>
                      <PresenceStatusBadge
                        status={row.status}
                        testId={`pw1-status-badge-${row.employee_id}`}
                      />
                    </td>
                    <td data-testid={`pw1-where-${row.employee_id}`}>
                      {row.where === null ? (
                        <p className="wp-muted">{t('presence.where.none')}</p>
                      ) : (
                        <span className="wp-presence__where">
                          <span>
                            {row.where.office === null
                              ? t('presence.where.noOffice')
                              : row.where.office.name}
                          </span>
                          <span className="wp-small wp-muted">
                            {row.where.within_geofence
                              ? t('presence.where.within')
                              : t('presence.where.outside')}
                          </span>
                          <Badge
                            severity="info"
                            icon="info"
                            label={t(PRESENCE_TRUST_LABEL[row.where.trust])}
                            testId={`pw1-trust-${row.employee_id}`}
                          />
                        </span>
                      )}
                    </td>
                    <td data-testid={`pw1-onwhat-${row.employee_id}`}>
                      {row.on_what === null
                        ? t('presence.onWhat.none')
                        : (row.on_what.task_title ?? row.on_what.achau_task_id)}
                    </td>
                    <td data-testid={`pw1-lastseen-${row.employee_id}`}>
                      {row.last_seen_at === null
                        ? t('common.never')
                        : formatInstantWithAge(
                            row.last_seen_at,
                            locale,
                            timezone,
                            t('common.never'),
                          )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </div>
  );
}
